import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  LuMenu, LuX, LuBatteryCharging, LuMusic, LuDroplet, 
  LuBluetooth, LuLeaf, LuTent, LuSun, LuBike, LuChevronDown 
} from "react-icons/lu";
import { FiArrowLeft, FiHome } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

function SplitTextHover({ text, isHovered }) {
  return (
    <span className="inline-flex overflow-hidden relative">
      {text.split("").map((char, index) => {
        const displayChar = char === " " ? "\u00A0" : char;
        return (
          <span key={index} className="relative inline-block overflow-hidden">
            <motion.span
              animate={{ y: isHovered ? "100%" : "0%" }}
              transition={{
                duration: 0.35,
                ease: [0.25, 1, 0.5, 1],
                delay: index * 0.025
              }}
              className="block"
            >
              {displayChar}
            </motion.span>
            <motion.span
              initial={{ y: "-100%" }}
              animate={{ y: isHovered ? "0%" : "-100%" }}
              transition={{
                duration: 0.35,
                ease: [0.25, 1, 0.5, 1],
                delay: index * 0.025
              }}
              className="absolute left-0 top-0 block"
            >
              {displayChar}
            </motion.span>
          </span>
        );
      })}
    </span>
  );
}

export default function EchoxPage({ className }) {
  const navigate = useNavigate();
  const menuItems = ["Products", "Features", "Technology", "Support"];
  const videoRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isBuyHovered, setIsBuyHovered] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    let animFrameId;
    const update = () => {
      if (videoRef.current) {
        setCurrentTime(videoRef.current.currentTime);
      }
      animFrameId = requestAnimationFrame(update);
    };
    animFrameId = requestAnimationFrame(update);
    return () => cancelAnimationFrame(animFrameId);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.muted = true;
    
    // Force browser to load video metadata eagerly
    video.load();

    const lenis = new Lenis({
      wheelMultiplier: 0.65,
      touchMultiplier: 1.0
    });

    lenis.on("scroll", ScrollTrigger.update);

    const tickerCallback = (time) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(tickerCallback);
    gsap.ticker.lagSmoothing(0);

    // Use gsap.context to ensure proper DOM cleanup when React unmounts
    let ctx = gsap.context(() => {
      const playhead = { progress: 0 };
      let rAFActive = false;

      gsap.to(playhead, {
        progress: 1,
        ease: "none",
        scrollTrigger: {
          trigger: "#hero-section",
          start: "top top",
          end: "+=8500",
          pin: true,
          scrub: 1.5,
        },
        onUpdate: () => {
          // Check if video duration is valid before trying to seek
          if (video && !isNaN(video.duration) && video.duration > 0) {
            if (!rAFActive) {
              rAFActive = true;
              requestAnimationFrame(() => {
                video.currentTime = playhead.progress * video.duration;
                rAFActive = false;
              });
            }
          }
        },
      });
    });

    return () => {
      gsap.ticker.remove(tickerCallback);
      lenis.destroy();
      ctx.revert(); // This instantly unwraps the GSAP pin-spacer, preventing the React removeChild error
    };
  }, []);

  const specsWithIcons = [
    { value: "30H", label: "Battery Life", icon: LuBatteryCharging },
    { value: "360\u00B0", label: "Immersive Sound", icon: LuMusic },
    { value: "IPX7", label: "Waterproof", icon: LuDroplet },
    { value: "BT 5.4", label: "Ultra Connection", icon: LuBluetooth },
    { value: "100%", label: "Eco Material", icon: LuLeaf },
  ];

  const cardWidth = isMobile ? 260 : 350;
  const cardGap = isMobile ? 16 : 24;
  const cardHeight = isMobile ? 180 : 240;
  const trackDistance = -6 * (cardWidth + cardGap);

  const topReviews = [
    { text: "THE BASS IS ABSOLUTELY UNBELIEVABLE FOR A SPEAKER OF THIS SIZE!", author: "LIAM G." },
    { text: "IPX7 WATERPROOF RATING SECURED IT IN EVERY HEAVY STORM.", author: "MARCUS T." },
    { text: "ECOLOGICAL MATERIALS INTEGRATED PERFECTLY WITH THE PREMIUM DESIGN.", author: "CHLOE R." },
    { text: "THE SOUNDSTAGE IS WIDE, CRISP, AND FULL OF DETAIL.", author: "SARAH P." },
    { text: "INCREDIBLY RUGGED BUILD. FEELS LIKE IT CAN SURVIVE ANYTHING.", author: "DAVID K." },
    { text: "PERFECT INDOOR COMPANION THAT BLENDS WITH MODERN INTERIORS.", author: "AUSTIN F." },
    { text: "THE BASS IS ABSOLUTELY UNBELIEVABLE FOR A SPEAKER OF THIS SIZE!", author: "LIAM G." },
    { text: "IPX7 WATERPROOF RATING SECURED IT IN EVERY HEAVY STORM.", author: "MARCUS T." },
    { text: "ECOLOGICAL MATERIALS INTEGRATED PERFECTLY WITH THE PREMIUM DESIGN.", author: "CHLOE R." },
    { text: "THE SOUNDSTAGE IS WIDE, CRISP, AND FULL OF DETAIL.", author: "SARAH P." },
    { text: "INCREDIBLY RUGGED BUILD. FEELS LIKE IT CAN SURVIVE ANYTHING.", author: "DAVID K." },
    { text: "PERFECT INDOOR COMPANION THAT BLENDS WITH MODERN INTERIORS.", author: "AUSTIN F." },
  ];

  const bottomReviews = [
    { text: "30H BATTERY LIFE IS AN ABSOLUTE GAME CHANGER FOR CAMPING.", author: "SOPHIA L." },
    { text: "THE COMPACT FORM FACTOR MAKES IT THE PERFECT TRAVEL COMPANION.", author: "ALEX M." },
    { text: "FASTEST BLUETOOTH PAIRING I HAVE EVER EXPERIENCED.", author: "DANIEL S." },
    { text: "THE ECO-FRIENDLY RECYCLED PLASTIC FEELS AMAZING IN HAND.", author: "EMMA W." },
    { text: "STUNNING HIGHS AND DEEP LOWS WITH ZERO DISTORTION.", author: "JAMES B." },
    { text: "TRULY PREMIUM QUALITY FINISH EXCEEDING ALL MY EXPECTATIONS.", author: "NATHAN H." },
    { text: "30H BATTERY LIFE IS AN ABSOLUTE GAME CHANGER FOR CAMPING.", author: "SOPHIA L." },
    { text: "THE COMPACT FORM FACTOR MAKES IT THE PERFECT TRAVEL COMPANION.", author: "ALEX M." },
    { text: "FASTEST BLUETOOTH PAIRING I HAVE EVER EXPERIENCED.", author: "DANIEL S." },
    { text: "THE ECO-FRIENDLY RECYCLED PLASTIC FEELS AMAZING IN HAND.", author: "EMMA W." },
    { text: "STUNNING HIGHS AND DEEP LOWS WITH ZERO DISTORTION.", author: "JAMES B." },
    { text: "TRULY PREMIUM QUALITY FINISH EXCEEDING ALL MY EXPECTATIONS.", author: "NATHAN H." },
  ];

  const faqs = [
    { q: "How long does the battery last?", a: "ECHOX delivers up to 30 hours of continuous playtime on a single charge with active battery optimization." },
    { q: "Is it completely waterproof?", a: "Yes, featuring an IPX7 waterproof rating, it can survive underwater immersion up to 1 meter for 30 minutes." },
    { q: "What eco-friendly materials are used?", a: "ECHOX is crafted from 100% recycled ocean plastics, post-consumer fabrics, and bio-degradable packaging." },
    { q: "Can I connect multiple speakers?", a: "Absolutely. True Wireless Stereo (TWS) lets you connect two ECHOX speakers instantly for double the audio stage." },
    { q: "Does it support smart voice assistants?", a: "Yes, the integrated high-definition microphone supports seamless integration with Siri and Google Assistant." },
    { q: "What is the Bluetooth range?", a: "Equipped with Bluetooth 5.4 technology, ECHOX provides an ultra-stable connection with zero delay up to 50 feet." },
  ];

  return (
    <div className="echox-page-wrapper bg-[#131d2f] min-h-screen">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Anybody:wght@400;500;600;700;900&display=swap" rel="stylesheet" />

      {/* Floating Back Button */}
      <button 
        onClick={() => navigate('/portfolio')}
        className="fixed bottom-6 right-6 z-[100] bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all shadow-2xl cursor-pointer"
        style={{ fontFamily: "Anybody, sans-serif" }}
      >
        <FiArrowLeft /> Head back to Hostfig.com
      </button>

      <div id="hero-section" className={"relative w-screen h-screen overflow-hidden select-none bg-[#131d2f] " + (className || "")}>
        <video 
          ref={videoRef} 
          id="bg-video" 
          className="absolute inset-0 w-full h-full object-cover" 
          src="https://cdn.jiro.build/Speaker%20Ecommerce%20-%20ECHOX/Video/Speaker_Ecommerce_Product%20Update.mp4" 
          preload="auto" 
          muted 
          playsInline 
        />
        
        <div id="vignette-overlay" className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/30 pointer-events-none" />

        <header id="navbar-header" className="absolute top-0 left-0 right-0 z-20 flex justify-between items-center py-6 md:py-8 px-6 md:px-20">
          <motion.div 
            id="logo-wrapper"
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
          >
            <img 
              id="echox-logo-img"
              src="https://cdn.jiro.build/Speaker%20Ecommerce%20-%20ECHOX/Logos/ECHOX%20Logo.svg" 
              alt="ECHOX Logo" 
              className="w-auto" 
              style={{ height: "27px" }}
              referrerPolicy="no-referrer"
            />
          </motion.div>

          <motion.nav 
            id="menu-box-nav"
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="hidden md:inline-flex"
            style={{
              padding: "18px 24px",
              justifyContent: "center",
              alignItems: "center",
              gap: "35px",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.10)",
              backdropFilter: "blur(6.099999904632568px)",
              WebkitBackdropFilter: "blur(6.099999904632568px)",
            }}
          >
            {menuItems.map((item, index) => (
              <button
                key={item}
                id={"menu-item-" + index}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="cursor-pointer focus:outline-none"
                style={{
                  color: "#FFF",
                  textAlign: "center",
                  fontFamily: "Anybody, sans-serif",
                  fontSize: "15px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "normal",
                  overflow: "hidden",
                }}
              >
                <SplitTextHover text={item} isHovered={hoveredIndex === index} />
              </button>
            ))}
          </motion.nav>

          <motion.div 
            id="buy-now-wrapper"
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="hidden md:block"
          >
            <button
              id="buy-now-button"
              onMouseEnter={() => setIsBuyHovered(true)}
              onMouseLeave={() => setIsBuyHovered(false)}
              className="hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer focus:outline-none"
              style={{
                display: "inline-flex",
                padding: "18px 24px",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                borderRadius: "12px",
                background: "#B6CEE8",
                backdropFilter: "blur(6.099999904632568px)",
                WebkitBackdropFilter: "blur(6.099999904632568px)",
                color: "#131D2F",
                textAlign: "center",
                fontFamily: "Anybody, sans-serif",
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                border: "none",
                overflow: "hidden",
              }}
            >
               <SplitTextHover text="Buy Now" isHovered={isBuyHovered} />
            </button>
          </motion.div>

          <motion.div 
            id="mobile-menu-toggle-wrapper"
            initial={{ opacity: 0, x: 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="block md:hidden"
          >
            <button 
              id="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-white flex items-center justify-center p-3 rounded-xl bg-white/10 backdrop-blur-md hover:bg-white/20 transition-all cursor-pointer focus:outline-none"
            >
              {isMobileMenuOpen ? <LuX size={20} /> : <LuMenu size={20} />}
            </button>
          </motion.div>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                key="mobile-dropdown"
                id="mobile-dropdown-menu"
                initial={{ opacity: 0, y: -15, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -15, scale: 0.95 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="absolute top-24 left-6 right-6 z-30 flex flex-col p-6 rounded-2xl bg-[#131d2f]/95 backdrop-blur-xl border border-white/10 shadow-2xl pointer-events-auto"
              >
                <div className="flex flex-col gap-4">
                  {menuItems.map((item, index) => (
                    <button
                      key={item}
                      id={"mobile-menu-item-" + index}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="w-full text-left py-2 hover:opacity-80 transition-opacity cursor-pointer focus:outline-none text-white"
                      style={{ fontFamily: "Anybody, sans-serif", fontSize: "18px", fontWeight: 500, textTransform: "uppercase" }}
                    >
                      {item}
                    </button>
                  ))}
                  <div className="h-px bg-white/10 my-2" />
                  <button 
                    id="mobile-buy-now-button"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="w-full hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 cursor-pointer focus:outline-none py-4 px-6 rounded-xl flex justify-center items-center text-center text-[#131D2F]"
                    style={{ background: "#B6CEE8", fontFamily: "Anybody, sans-serif", fontSize: "15px", fontWeight: 600, textTransform: "uppercase", border: "none" }}
                  >
                    Buy Now
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        <div id="hero-content-wrapper" className="absolute inset-0 z-10 pointer-events-none">
          <motion.div 
            id="hero-title-container"
            animate={{ x: currentTime >= 1 ? -600 : 0, opacity: currentTime >= 1 ? 0 : 1 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              top: isMobile ? "120px" : "205px",
              left: isMobile ? "24px" : "80px",
              right: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "691px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
            }}
          >
            <div id="hero-title-group" style={{ textAlign: isMobile ? "center" : "left", display: "flex", flexDirection: "column", alignItems: isMobile ? "center" : "flex-start" }}>
              <span 
                id="hero-title-sm"
                style={{ 
                  color: "#FFF", 
                  fontFamily: "Anybody, sans-serif", 
                  fontSize: isMobile ? "15px" : "23.065px", 
                  fontStyle: "normal", 
                  fontWeight: 400, 
                  lineHeight: "normal", 
                  textTransform: "uppercase",
                  marginBottom: isMobile ? "4px" : "10px",
                }}
              >
                MASSIVE SOUND WITH SMALL
              </span>
              <h1 
                id="hero-title-h1"
                style={{ 
                  color: "#FFF", 
                  fontFamily: "Anybody, sans-serif", 
                  fontSize: isMobile ? "42px" : "100px", 
                  fontStyle: "normal", 
                  fontWeight: 900, 
                  lineHeight: isMobile ? "42px" : "100px", 
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                speaker
              </h1>
            </div>
          </motion.div>

          <motion.div 
            id="state-1-5-unified-container"
            initial={{ x: -600, opacity: 0 }}
            animate={{ x: currentTime >= 2 && currentTime < 3 ? 0 : -600, opacity: currentTime >= 2 && currentTime < 3 ? 1 : 0 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              top: isMobile ? "120px" : "205px",
              left: isMobile ? "24px" : "80px",
              right: isMobile ? "24px" : "80px",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
          >
            <h1 
              id="powerful-sound-heading"
              style={{
                color: "#FFF",
                textAlign: isMobile ? "center" : "left",
                fontFamily: "Anybody, sans-serif",
                fontSize: isMobile ? "32px" : "60px",
                fontStyle: "normal",
                fontWeight: "bold",
                lineHeight: "normal",
                textTransform: "uppercase",
                margin: 0,
                marginBottom: isMobile ? "24px" : "40px",
              }}
            >
              Powerful Sound. <br /> Built For Adventure.
            </h1>
            
            <div 
              id="glass-specs-container"
              style={{
                width: "100%",
                display: isMobile ? "grid" : "flex",
                gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "none",
                justifyContent: isMobile ? "stretch" : "space-between",
                gap: isMobile ? "16px" : "24px",
                alignItems: "stretch",
              }}
            >
              {specsWithIcons.map((spec, index) => {
                const IconComponent = spec.icon;
                return (
                  <div 
                    key={index}
                    id={"glass-spec-item-" + index}
                    className="flex flex-col items-center text-center"
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      borderRadius: "20px",
                      padding: isMobile ? "20px 12px" : "36px 20px",
                      flex: 1,
                      minWidth: isMobile ? "0" : "180px",
                      height: isMobile ? "170px" : "280px",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div 
                      id={"glass-spec-icon-wrapper-" + index}
                      className="rounded-full border border-white/20 flex items-center justify-center text-white/80"
                      style={{ 
                        width: isMobile ? "56px" : "80px", 
                        height: isMobile ? "56px" : "80px", 
                        marginBottom: isMobile ? "16px" : "24px" 
                      }}
                    >
                      <IconComponent size={isMobile ? 24 : 32} strokeWidth={1.5} />
                    </div>
                    
                    <span 
                      id={"glass-spec-value-" + index}
                      style={{ 
                        alignSelf: "stretch", 
                        color: "#FFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: isMobile ? "24px" : "50px", 
                        fontStyle: "normal", 
                        fontWeight: 400, 
                        lineHeight: "normal", 
                        textTransform: "uppercase" 
                      }}
                    >
                      {spec.value}
                    </span>
                    <span 
                      id={"glass-spec-label-" + index}
                      style={{ 
                        color: "#D9EAFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: isMobile ? "14px" : "24px", 
                        fontStyle: "normal", 
                        fontWeight: 400, 
                        lineHeight: isMobile ? "18px" : "30px", 
                        textTransform: "uppercase",
                        margin: "6px"
                      }}
                    >
                      {spec.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div 
            id="bass-tech-heading-container"
            initial={{ x: -600, opacity: 0 }}
            animate={{ x: currentTime >= 3 && currentTime < 4 ? 0 : -600, opacity: currentTime >= 3 && currentTime < 4 ? 1 : 0 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              top: isMobile ? "140px" : "225px",
              left: isMobile ? "24px" : "80px",
              right: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "520px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
            }}
          >
            <h1 
              id="bass-tech-heading"
              style={{ 
                color: "#FFF", 
                fontFamily: "Anybody, sans-serif", 
                fontSize: isMobile ? "34px" : "60px", 
                fontStyle: "normal", 
                fontWeight: "bold", 
                lineHeight: "1.1", 
                textTransform: "uppercase",
                textAlign: isMobile ? "center" : "left"
              }}
            >
              {isMobile ? (
                "Bass Technology & Smart Voice Assistant"
              ) : (
                <>Bass Technology <br /> & Smart Voice <br /> Assistant</>
              )}
            </h1>
          </motion.div>

          <motion.div 
            id="right-side-desc-container"
            initial={{ x: 600, opacity: 0 }}
            animate={{ x: currentTime >= 3 && currentTime < 4 ? 0 : 600, opacity: currentTime >= 3 && currentTime < 4 ? 1 : 0 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              bottom: isMobile ? "40px" : "80px",
              right: isMobile ? "24px" : "80px",
              left: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "438px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
            }}
          >
            <p 
              id="right-side-desc-text"
              style={{ 
                color: "#D9EAFF",
                textAlign: isMobile ? "center" : "right",
                fontFamily: "Anybody, sans-serif", 
                fontSize: isMobile ? "16px" : "24px", 
                fontStyle: "normal", 
                fontWeight: 400, 
                lineHeight: isMobile ? "22px" : "30px", 
                textTransform: "uppercase" 
              }}
            >
              Experience rich, immersive audio in a compact design engineered for every journey. From mountain peaks to city streets.
            </p>
          </motion.div>

          <motion.div 
            id="state-2-2-left-heading-container"
            initial={{ x: -600, opacity: 0 }}
            animate={{ x: currentTime >= 5 && currentTime < 6 ? 0 : -600, opacity: currentTime >= 5 && currentTime < 6 ? 1 : 0 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              top: isMobile ? "140px" : "225px",
              left: isMobile ? "24px" : "80px",
              right: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "520px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
              pointerEvents: currentTime >= 5 && currentTime < 6 ? "auto" : "none",
            }}
          >
            <h1 
              id="state-2-2-massive-sound-heading"
              style={{ 
                color: "#FFF", 
                fontFamily: "Anybody, sans-serif", 
                fontSize: isMobile ? "34px" : "60px", 
                fontStyle: "normal", 
                fontWeight: "bold", 
                lineHeight: "1.1", 
                textTransform: "uppercase",
                textAlign: isMobile ? "center" : "left"
              }}
            >
              {isMobile ? (
                "Small Speaker. Massive Sound."
              ) : (
                <>Small Speaker. <br /> Massive Sound.</>
              )}
            </h1>
          </motion.div>

          <motion.div 
            id="state-2-2-right-cards-container"
            initial={{ x: 600, opacity: 0 }}
            animate={{ x: currentTime >= 5 && currentTime < 6 ? 0 : 600, opacity: currentTime >= 5 && currentTime < 6 ? 1 : 0 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              bottom: isMobile ? "40px" : "80px",
              right: isMobile ? "24px" : "80px",
              left: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "580px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
              pointerEvents: currentTime >= 5 && currentTime < 6 ? "auto" : "none",
            }}
          >
            <div id="adventure-grid-container" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: isMobile ? "16px" : "24px" }}>
              {[
                { label: "Camping", icon: LuTent },
                { label: "Beach", icon: LuSun },
                { label: "Cycling", icon: LuBike },
                { label: "Home Office", icon: FiHome },
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <motion.div 
                    key={index}
                    id={"adventure-card-" + index}
                    whileHover={{ y: -6, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                    style={{
                      background: "rgba(255, 255, 255, 0.15)",
                      border: "1px solid rgba(255, 255, 255, 0.12)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      borderRadius: "24px",
                      padding: isMobile ? "16px 12px" : "36px 24px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      height: isMobile ? "140px" : "200px",
                      boxSizing: "border-box",
                      cursor: "pointer",
                    }}
                  >
                    <div 
                      id={"adventure-card-icon-wrapper-" + index}
                      className="rounded-full border border-white/20 flex items-center justify-center text-white/80"
                      style={{ 
                        width: isMobile ? "56px" : "80px", 
                        height: isMobile ? "56px" : "80px", 
                        marginBottom: isMobile ? "12px" : "20px" 
                      }}
                    >
                      <IconComponent size={isMobile ? 24 : 32} strokeWidth={1.5} />
                    </div>
                    <span 
                      id={"adventure-card-label-" + index}
                      style={{ 
                        color: "#FFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: isMobile ? "16px" : "24px", 
                        fontStyle: "normal", 
                        fontWeight: "bold", 
                        lineHeight: "normal", 
                        textTransform: "uppercase",
                        letterSpacing: "0.05em"
                      }}
                    >
                      {item.label}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div 
            id="top-horizontal-reviews-container"
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: currentTime >= 6 && currentTime < 7 ? 0 : -100, opacity: currentTime >= 6 && currentTime < 7 ? 1 : 0 }} 
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto overflow-hidden"
            style={{
              top: "calc(50% - " + (cardHeight + 10) + "px)",
              left: 0,
              width: "100%",
              height: (cardHeight + 20) + "px",
              maskImage: "linear-gradient(to right, transparent, #000 15%, #000 85%, transparent)",
              WebkitMaskImage: "linear-gradient(to right, transparent, #000 15%, #000 85%, transparent)",
            }}
          >
            <motion.div 
              id="top-reviews-track"
              style={{ display: "flex", flexDirection: "row", gap: cardGap + "px", width: (12 * (cardWidth + cardGap)) + "px" }}
              animate={{ x: [trackDistance, 0] }}
              transition={{ repeat: Infinity, repeatType: "loop", duration: 24, ease: "linear" }}
            >
              {topReviews.map((review, index) => (
                 <div 
                  key={"top-review-" + index}
                  id={"review-card-top-" + index}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "none",
                    borderRadius: "20px",
                    padding: isMobile ? "20px" : "28px 24px",
                    width: cardWidth + "px",
                    height: cardHeight + "px",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                 >
                   <div style={{ color: "#FFD700", fontSize: "20px" }}>★ ★ ★ ★ ★</div>
                   <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                     <p 
                      style={{ 
                        color: "#FFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: isMobile ? "16px" : "20px", 
                        fontStyle: "normal", 
                        fontWeight: 400, 
                        lineHeight: "1.4", 
                        textTransform: "uppercase",
                        margin: 0,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical"
                      }}
                     >
                       "{review.text}"
                     </p>
                     <span style={{ color: "#D9EAFF", fontFamily: "Anybody, sans-serif", fontSize: "10px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal", textTransform: "uppercase", opacity: 0.8 }}>
                       {"— " + review.author}
                     </span>
                   </div>
                 </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div 
            id="bottom-horizontal-reviews-container"
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: currentTime >= 6 && currentTime < 7 ? 0 : 100, opacity: currentTime >= 6 && currentTime < 7 ? 1 : 0 }} 
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto overflow-hidden"
            style={{
              top: "calc(50% + 10px)",
              left: 0,
              width: "100%",
              height: (cardHeight + 20) + "px",
              maskImage: "linear-gradient(to right, transparent, #000 15%, #000 85%, transparent)",
              WebkitMaskImage: "linear-gradient(to right, transparent, #000 15%, #000 85%, transparent)",
            }}
          >
            <motion.div 
              id="bottom-reviews-track"
              style={{ display: "flex", flexDirection: "row", gap: cardGap + "px", width: (12 * (cardWidth + cardGap)) + "px" }}
              animate={{ x: [0, trackDistance] }}
              transition={{ repeat: Infinity, repeatType: "loop", duration: 24, ease: "linear" }}
            >
              {bottomReviews.map((review, index) => (
                 <div 
                  key={"bottom-review-" + index}
                  id={"review-card-bottom-" + index}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "none",
                    borderRadius: "20px",
                    padding: isMobile ? "20px" : "28px 24px",
                    width: cardWidth + "px",
                    height: cardHeight + "px",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    flexShrink: 0,
                  }}
                 >
                   <div style={{ color: "#FFD700", fontSize: "20px" }}>★ ★ ★ ★ ★</div>
                   <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                     <p 
                      style={{ 
                        color: "#FFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: isMobile ? "16px" : "20px", 
                        fontStyle: "normal", 
                        fontWeight: 400, 
                        lineHeight: "1.4", 
                        textTransform: "uppercase",
                        margin: 0,
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical"
                      }}
                     >
                       "{review.text}"
                     </p>
                     <span style={{ color: "#D9EAFF", fontFamily: "Anybody, sans-serif", fontSize: "10px", fontStyle: "normal", fontWeight: 400, lineHeight: "normal", textTransform: "uppercase", opacity: 0.8 }}>
                       {"— " + review.author}
                     </span>
                   </div>
                 </div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div 
            id="hero-paragraph-container"
            initial={{ x: 0, opacity: 1 }}
            animate={{ x: currentTime >= 1 ? 600 : 0, opacity: currentTime >= 1 ? 0 : 1 }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              bottom: isMobile ? "40px" : "80px",
              right: isMobile ? "24px" : "80px",
              left: isMobile ? "24px" : "auto",
              width: isMobile ? "auto" : "438px",
              maxWidth: isMobile ? "none" : "calc(100% - 160px)",
            }}
          >
            <p 
              id="hero-paragraph-text"
              style={{ 
                color: "#D9EAFF", 
                fontFamily: "Anybody, sans-serif", 
                fontSize: isMobile ? "16px" : "24px", 
                fontStyle: "normal", 
                fontWeight: 400, 
                lineHeight: isMobile ? "22px" : "30px", 
                textTransform: "uppercase",
                textAlign: isMobile ? "center" : "right"
              }}
            >
              Experience rich, immersive audio in a compact design engineered for every journey. From mountain peaks to city streets, 
            </p>
          </motion.div>

          <motion.div 
            id="state-3-faq-container"
            initial={{ x: isMobile ? 0 : "-50%", y: isMobile ? 150 : "calc(-50% + 150px)", opacity: 0 }}
            animate={{ 
              x: isMobile ? 0 : "-50%", 
              y: currentTime >= 7 && currentTime < 8 ? (isMobile ? 0 : "-50%") : (isMobile ? 150 : "calc(-50% + 150px)"), 
              opacity: currentTime >= 7 && currentTime < 8 ? 1 : 0,
            }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              top: isMobile ? "90px" : "50%",
              left: isMobile ? "16px" : "50%",
              right: isMobile ? "16px" : "auto",
              width: isMobile ? "auto" : "90%",
              maxWidth: "850px",
              height: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: currentTime >= 7 && currentTime < 8 ? "auto" : "none",
            }}
          >
            <div id="faq-header-wrapper" className="flex flex-col items-center" style={{ marginBottom: isMobile ? "16px" : "24px", textAlign: "center" }}>
              <h2 
                id="faq-section-title"
                style={{ 
                  color: "#FFF", 
                  fontFamily: "Anybody, sans-serif", 
                  fontSize: isMobile ? "32px" : "60px", 
                  fontWeight: 900, 
                  lineHeight: "1.1", 
                  textTransform: "uppercase", 
                  margin: 0,
                  letterSpacing: "0.02em"
                }}
              >
                Frequently Asked Questions
              </h2>
            </div>
            
            <div 
              id="faq-grid-container"
              style={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: isMobile ? "10px" : "12px",
                maxHeight: isMobile ? "calc(100vh - 230px)" : "calc(100vh - 260px)",
                overflowY: "auto",
                paddingRight: "4px",
              }}
            >
              {faqs.map((item, index) => (
                <motion.div 
                  key={index}
                  id={"faq-row-card-" + index}
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  style={{
                    background: "rgba(255, 255, 255, 0.15)",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: isMobile ? "16px 18px" : "16px 24px",
                    height: "auto",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    transition: "padding 0.2s ease",
                  }}
                >
                  <div 
                    id={"faq-row-header-" + index}
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      width: "100%",
                      minHeight: "24px"
                    }}
                  >
                    <span 
                      id={"faq-question-text-" + index}
                      style={{ 
                        color: "#FFF", 
                        fontFamily: "Anybody, sans-serif", 
                        fontSize: "16px", 
                        fontWeight: 500, 
                        lineHeight: "1.2", 
                        textTransform: "uppercase",
                        textAlign: "left",
                        width: "100%",
                        paddingLeft: "0px",
                        paddingRight: "32px"
                      }}
                    >
                      {item.q}
                    </span>
                    <motion.div 
                      id={"faq-chevron-wrapper-" + index}
                      animate={{ rotate: expandedFaq === index ? 180 : 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      style={{ 
                        position: "absolute", 
                        right: "0px",
                        top: "50%",
                        y: "-50%",
                        display: "flex", 
                        alignItems: "center" 
                      }}
                    >
                      <LuChevronDown size={isMobile ? 18 : 24} className="text-white/80" />
                    </motion.div>
                  </div>
                  
                  <AnimatePresence initial={false}>
                    {expandedFaq === index && (
                      <motion.div
                        key={"faq-answer-" + index}
                        id={"faq-answer-container-" + index}
                        initial={{ height: 0, opacity: 0, marginTop: 0 }}
                        animate={{ height: "auto", opacity: 1, marginTop: isMobile ? 6 : 10 }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <p 
                          id={"faq-answer-text-" + index}
                          style={{ 
                            color: "#D9EAFF", 
                            fontFamily: "Anybody, sans-serif", 
                            fontSize: isMobile ? "12px" : "14px", 
                            fontWeight: 400, 
                            lineHeight: "1.4", 
                            textTransform: "uppercase",
                            margin: 0,
                            textAlign: "left",
                            paddingLeft: "0px",
                            paddingRight: "32px"
                          }}
                        >
                          {item.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            id="state4-footer-container"
            initial={{ y: 200, opacity: 0 }}
            animate={{ 
              y: currentTime >= 9 ? 0 : 200, 
              opacity: currentTime >= 9 ? 1 : 0, 
            }} 
            transition={{ duration: 1.6, ease: [0.25, 1, 0.5, 1] }}
            className="absolute pointer-events-auto"
            style={{
              bottom: isMobile ? "12px" : "24px",
              left: isMobile ? "12px" : "24px",
              right: isMobile ? "12px" : "24px",
              background: "rgba(255, 255, 255, 0.08)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              borderRadius: isMobile ? "20px" : "32px",
              padding: isMobile ? "45px 24px 35px 24px" : "80px 60px 60px 60px",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "24px" : "40px",
              boxSizing: "border-box",
              pointerEvents: currentTime >= 9 ? "auto" : "none",
            }}
          >
            <div id="footer-top-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <span id="footer-copyright" style={{ color: "rgba(255, 255, 255, 0.6)", fontFamily: "Anybody, sans-serif", fontSize: isMobile ? "12px" : "14px", fontWeight: 400, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                © Copyright 2026
              </span>
              <div id="footer-divider-line" style={{ flex: 1, height: "1px", background: "rgba(255, 255, 255, 0.15)", marginLeft: isMobile ? "12px" : "24px", marginRight: isMobile ? "12px" : "24px" }} />
              <span id="footer-rights" style={{ color: "rgba(255, 255, 255, 0.6)", fontFamily: "Anybody, sans-serif", fontSize: isMobile ? "12px" : "14px", fontWeight: 400, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                All Rights Reserved
              </span>
            </div>
            
            <div id="footer-nav-row" style={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: isMobile ? "10px 16px" : "40px", width: "100%" }}>
              {["Home", "About", "Service", "Platform", "How It Works", "Contact"].map((item, index) => (
                <a 
                  key={item} 
                  id={"footer-nav-link-" + index}
                  href={"#" + item.toLowerCase().replace(/\s+/g, "-")} 
                  style={{ color: "rgba(255, 255, 255, 0.8)", fontFamily: "Anybody, sans-serif", fontSize: isMobile ? "14px" : "16px", fontWeight: 500, textTransform: "uppercase", textDecoration: "none", letterSpacing: "0.05em", transition: "color 0.2s ease" }}
                  className="hover:text-white"
                >
                  {item}
                </a>
              ))}
            </div>

            <div id="footer-bottom-brand-row" style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", marginTop: isMobile ? "0px" : "4px" }}>
              <img 
                id="footer-echox-logo-img"
                src="https://cdn.jiro.build/Speaker%20Ecommerce%20-%20ECHOX/Logos/ECHOX%20Logo.svg" 
                alt="ECHOX Logo" 
                className="w-auto" 
                style={{ height: isMobile ? "70px" : "180px", opacity: 0.95 }}
                referrerPolicy="no-referrer"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}